import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";

const VALID_SECTIONS = [
  "overview",
  "sales",
  "products",
  "tables",
  "kitchen",
  "cashier",
  "delivery",
  "stock",
  "staff",
  "reservations",
  "clients",
];

/**
 * Reduce un payload analítico a un summary compacto.
 * Sin esto, mandar todo el JSON saturaría el contexto del LLM.
 */
function summarize(section: string, data: any): unknown {
  if (!data || typeof data !== "object") return data;
  const summary: Record<string, unknown> = {
    range: data.range,
    kpis: data.kpis,
  };
  // Solo incluir top items / series resumidas
  if (data.topProducts) summary.topProducts = data.topProducts.slice(0, 5);
  if (data.topByRevenue) summary.topByRevenue = data.topByRevenue.slice(0, 5);
  if (data.topByFrequency)
    summary.topByFrequency = data.topByFrequency.slice(0, 5);
  if (data.topDebtors) summary.topDebtors = data.topDebtors.slice(0, 5);
  if (data.topTables) summary.topTables = data.topTables.slice(0, 5);
  if (data.members) summary.topMembers = data.members.slice(0, 5);
  if (data.byStatus) summary.byStatus = data.byStatus;
  if (data.byPaymentMethod) summary.byPaymentMethod = data.byPaymentMethod;
  if (data.byOrderType) summary.byOrderType = data.byOrderType;
  if (data.lowStock) summary.lowStock = data.lowStock.slice(0, 10);
  if (data.outOfStock) summary.outOfStock = data.outOfStock.slice(0, 10);
  // Series temporales: solo total + número de buckets
  if (Array.isArray(data.series)) {
    summary.seriesPoints = data.series.length;
    summary.seriesTotal = data.series.reduce(
      (s: number, p: any) => s + (p.revenue ?? p.count ?? 0),
      0,
    );
  }
  return summary;
}

export const getAnalyticsSkill: Skill = {
  id: "getAnalytics",
  description:
    "Obtiene un reporte analítico de un restaurante en un rango de fechas. Úsala cuando el usuario pregunte por estadísticas, ingresos, ventas, comparativas, performance de productos, etc. Devuelve un resumen compacto con KPIs y tops.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string", description: "ID del restaurante" },
      section: {
        type: "string",
        enum: VALID_SECTIONS,
        description:
          "Sección del reporte: overview (general), sales, products, tables, kitchen, cashier, delivery, stock, staff, reservations, clients.",
      },
      from: {
        type: "string",
        description: "Fecha inicio ISO (YYYY-MM-DD). Default: hace 30 días.",
      },
      to: {
        type: "string",
        description: "Fecha fin ISO (YYYY-MM-DD). Default: hoy.",
      },
    },
    required: ["restaurantId", "section"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) {
      return { ok: false, error: "Sesión no disponible." };
    }
    const restaurantId = String(args.restaurantId ?? "");
    const section = String(args.section ?? "overview");
    if (!restaurantId) {
      return { ok: false, error: "Falta restaurantId." };
    }
    if (!VALID_SECTIONS.includes(section)) {
      return { ok: false, error: `Sección inválida: ${section}` };
    }
    if (ctx.follow.enabled) {
      await ctx.follow.step(`Consultando reporte: ${section}…`, {
        durationMs: 500,
      });
    }
    try {
      const params = new URLSearchParams();
      if (args.from) params.set("from", new Date(String(args.from)).toISOString());
      if (args.to) params.set("to", new Date(String(args.to)).toISOString());
      const { data } = await apiWithAuth.get(
        `/restaurants/${restaurantId}/analytics/${section}?${params.toString()}`,
        ctx.accessToken,
      );
      if (ctx.follow.enabled) {
        await ctx.follow.step("✓ Datos obtenidos", {
          durationMs: 500,
          spinner: false,
        });
        ctx.follow.clear();
      }
      return {
        ok: true,
        result: summarize(section, data),
        humanSummary: `Analítica '${section}' obtenida.`,
      };
    } catch (err) {
      return {
        ok: false,
        error: (err as Error)?.message ?? "Error obteniendo analítica.",
      };
    }
  },
};
